import type { FairValueHistogramBucket } from '../fairValueHistogramFragment';
import { Group } from '@visx/group';
import { Bar } from '@visx/shape';
import type { ScaleBand, ScaleLinear } from 'd3-scale';
import { cn } from '@simplywallst/ui-core/next';
import { Fragment, useId, useMemo } from 'react';

type Props = {
  xBandScale: ScaleBand<number>;
  yScale: ScaleLinear<number, number, never>;
  buckets: FairValueHistogramBucket[];
  height: number;
  selectedBucket: FairValueHistogramBucket | null;
};

export const FairValueHistogramChartBars = ({
  xBandScale,
  yScale,
  buckets,
  height,
  selectedBucket,
}: Props) => {
  const radialGradientId = useId();
  const maskId = useId();
  const bandwidth = xBandScale.bandwidth();
  const tallestBucketXPercentage = useMemo(() => {
    const tallestBucketX =
      xBandScale(buckets.reduce((a, b) => (a.count > b.count ? a : b)).min) ??
      0 + bandwidth / 2;

    const xMax = xBandScale.range()[1];
    return xMax
      ? Math.max(0, Math.min(100, (tallestBucketX / xMax) * 100))
      : 50;
  }, [buckets, bandwidth, xBandScale]);
  const padding = (xBandScale.padding() * bandwidth) / 2;

  return (
    <>
      <defs>
        <radialGradient
          id={radialGradientId}
          cx={`${tallestBucketXPercentage}%`}
          cy="50%"
          r="70%"
          fx={`${tallestBucketXPercentage}%`}
          fy="50%"
        >
          <stop
            offset="20%"
            stopColor="var(--s-color-p-grey-100)"
            stopOpacity="0"
          />
          <stop
            offset="100%"
            stopColor="var(--s-color-p-grey-100)"
            stopOpacity="0.6"
          />
        </radialGradient>
        <mask id={maskId}>
          {buckets.map((bucket, i) => (
            <Group key={`bar-${i}`}>
              <Bar
                x={xBandScale(bucket.min) ?? 0}
                y={yScale(bucket.count) + padding}
                width={bandwidth}
                height={Math.max(0, height - yScale(bucket.count) - padding)}
                className="fill-p-white-100"
              />
            </Group>
          ))}
        </mask>
      </defs>

      <Group>
        {buckets.map((bucket, i) => {
          const isSelected = selectedBucket?.min === bucket.min;
          const y = yScale(bucket.count);
          const x = xBandScale(bucket.min) ?? 0;

          return (
            <Fragment key={`bar-${i}`}>
              <rect
                x={x - padding}
                y={0}
                width={bandwidth + padding * 2}
                height={height}
                className={cn(
                  'transition-colors',
                  isSelected ? 'fill-p-grey-180 opacity-40' : 'fill-transparent'
                )}
              />
              <Bar
                x={x}
                y={y + padding}
                width={bandwidth}
                height={Math.max(0, height - y - padding)}
                className={cn(
                  'transition-colors',
                  isSelected ? 'fill-chart-03-soft' : 'fill-chart-03'
                )}
              />
            </Fragment>
          );
        })}
        <rect
          x="0"
          y="0"
          width="100%"
          height={height}
          fill={`url(#${radialGradientId})`}
          mask={`url(#${maskId})`}
        />
      </Group>
    </>
  );
};
